import { View } from "react-native";
import React, { useState } from "react";
import {
  updateGiftRequest,
  useFetchGiftHistoryQuery,
} from "queries/account.queries";
import { Divider, Text } from "@ui-kitten/components";
import MainContainer from "components/MainContainer";
import Button from "components/Button";
import Spacer from "components/Spacer";
import UpdateRecipientDialog from "./UpdateRecipientDialog";
import { useMutation } from "@tanstack/react-query";
import toast from "utils/toast";
import dayjs, { Dayjs } from "dayjs";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { getUserName } from "utils/userUtils";

export interface RecipientInfo {
  firstName: string;
  lastName: string;
  email: string;
  deliverAt: Dayjs;
}
const MyGifts = () => {
  const {
    data: gifts,
    isFetching,
    refetch: refetchGifts,
  } = useFetchGiftHistoryQuery();
  const [updateRecipientDialogVisible, setUpdateRecipientDialogVisible] =
    useState(false);
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const [giftToBeUpdated, setGiftToBeUpdated] = useState<string>("");
  const [originalRecipientInfo, setOriginalRecipientInfo] =
    useState<RecipientInfo>({
      firstName: "",
      lastName: "",
      email: "",
      deliverAt: dayjs(),
    });
  const { mutateAsync: updateRecipient, isLoading: loading } =
    useMutation(updateGiftRequest);

  const handleUpdateRecipient = async (recipient: RecipientInfo) => {
    await updateRecipient({
      code: giftToBeUpdated,
      toUser: recipient,
      deliverAt: dayjs(recipient.deliverAt).tz(tenantConfig?.timezone).utc(),
    });
    setUpdateRecipientDialogVisible(false);
    toast.success("Recipient info has been updated");
    refetchGifts();
  };

  return (
    <MainContainer>
      <View className="p-4">
        <Text category="h6">My Gifts</Text>
        {isFetching && <Text category="s1">Loading...</Text>}
        {gifts &&
          gifts.map((gift) => (
            <View key={gift.id} className="p-2">
              <View className="flex-row justify-between items-center">
                <View>
                  <Text category="s1">Gift Name: {gift.planPackage.name}</Text>
                  <Text>Quantity: {gift.quantity}</Text>
                </View>
                <View>
                  <Text status={gift.claimed ? "success" : "info"}>
                    {gift.claimed ? "claimed" : "pending"}
                  </Text>
                </View>
              </View>
              <View>
                {/* @ts-ignore */}
                <Text>Recipient: {getUserName(gift)}</Text>
                <Text>Recipient Email: {gift.email}</Text>
                <Text>
                  Delivery Date:{" "}
                  {dayjs(gift.deliverAt)
                    .tz(tenantConfig?.timezone)
                    .format("MM/DD/YYYY")}
                </Text>
              </View>
              {/* if gift is not claimed, allow user to edit recipient info */}
              {!gift.claimed && (
                <View>
                  <Spacer margin={4} />
                  <Button
                    size="small"
                    style={{ alignSelf: "flex-end" }}
                    onPress={() => {
                      setGiftToBeUpdated(gift.code);
                      setOriginalRecipientInfo({
                        firstName: gift.firstName,
                        lastName: gift.lastName,
                        email: gift.email,
                        deliverAt: dayjs(gift.deliverAt),
                      });
                      setUpdateRecipientDialogVisible(true);
                    }}
                  >
                    Update Recipient Info
                  </Button>
                </View>
              )}
              <Divider style={{ marginVertical: 8 }} />
            </View>
          ))}
      </View>
      <UpdateRecipientDialog
        originalRecipientInfo={originalRecipientInfo}
        onSubmit={handleUpdateRecipient}
        loading={loading}
        visible={updateRecipientDialogVisible}
        setVisible={setUpdateRecipientDialogVisible}
      />
    </MainContainer>
  );
};

export default MyGifts;
